import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterOption } from 'models/Filters';
import useGetFilterOptions from 'hooks/useGetFilterOptions';
import api from 'api';
import { sortOptions } from 'utils';
import useMultiSort from 'hooks/useMultiSort';
import {
  HEADING_TRANSLATIONS,
  MULTI_SORT_DIRECTION_COPY,
  SORTING_VALUES,
} from '../ContactsTable/components/ManagePeopleTableCells/data';

const NAME_FILTER_KEY = 'name';
const EMAIL_FILTER_KEY = 'email';
const COMPANY_FILTER_KEY = 'companyName';

export default function useContactsFilterOptions() {
  const { t } = useTranslation();

  const { getMultiSelectSearchableFilter } = useGetFilterOptions();

  const { multiSortDirectionTranslation } = useMultiSort(
    SORT_COLUMNS,
    SORTING_VALUES,
    HEADING_TRANSLATIONS,
    MULTI_SORT_DIRECTION_COPY,
  );

  const getNameOptions = useCallback(async (search: string) => {
    const { data } = await api.contacts.getContactNameOptions(search);

    return sortOptions(data);
  }, []);

  const getEmailOptions = useCallback(async (search: string) => {
    const { data } = await api.contacts.getContactEmailOptions(search);

    return sortOptions(data);
  }, []);

  const getCompanyOptions = useCallback(async (search: string) => {
    const { data } = await api.contacts.getContactCompanyOptions(search);

    return sortOptions(data);
  }, []);

  const nameFilter = useMemo(
    () =>
      getMultiSelectSearchableFilter(
        t('name'),
        NAME_FILTER_KEY,
        NAME_FILTER_KEY,
        getNameOptions,
      ),
    [getNameOptions, getMultiSelectSearchableFilter, t],
  );

  const emailFilter = useMemo(
    () =>
      getMultiSelectSearchableFilter(
        t('email'),
        EMAIL_FILTER_KEY,
        EMAIL_FILTER_KEY,
        getEmailOptions,
      ),
    [getEmailOptions, getMultiSelectSearchableFilter, t],
  );

  const companyFilter = useMemo(
    () =>
      getMultiSelectSearchableFilter(
        t('company'),
        COMPANY_FILTER_KEY,
        COMPANY_FILTER_KEY,
        getCompanyOptions,
      ),
    [getCompanyOptions, getMultiSelectSearchableFilter, t],
  );

  const preApprovedOptions = useMemo(
    () => [
      { value: 'true', label: t('approved') },
      { value: 'false', label: t('notApproved') },
    ],
    [t],
  );

  const existUserOptions = useMemo(
    () => [
      { value: 'true', label: t('hasResolveAccount') },
      { value: 'false', label: t('noResolveAccount') },
    ],
    [t],
  );

  const preApprovedFilter = useMemo(
    () => ({
      value: 'preApproved',
      label: t('preApproved'),
      options: preApprovedOptions,
    }),
    [preApprovedOptions, t],
  );

  const existUserFilter = useMemo(
    () => ({
      value: 'existUser',
      label: t('resolveAccount'),
      options: existUserOptions,
    }),
    [existUserOptions, t],
  );

  const filterOptions = useMemo(
    () =>
      [
        nameFilter,
        emailFilter,
        companyFilter,
        preApprovedFilter,
        existUserFilter,
      ] as FilterOption[],
    [
      companyFilter,
      emailFilter,
      existUserFilter,
      nameFilter,
      preApprovedFilter,
    ],
  );

  const columnFilterOptions = useMemo(
    () => ({
      firstName: { options: nameFilter as FilterOption },
      email: { options: emailFilter as FilterOption },
      companyName: { options: companyFilter as FilterOption },
      preApproved: {
        options: preApprovedFilter as FilterOption,
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.preApproved],
      },
      existUser: {
        options: existUserFilter as FilterOption,
        sortDirectionTranslation:
          multiSortDirectionTranslation[SORTING_VALUES.existUser],
      },
    }),
    [
      companyFilter,
      emailFilter,
      existUserFilter,
      multiSortDirectionTranslation,
      nameFilter,
      preApprovedFilter,
    ],
  );

  return { filterOptions, columnFilterOptions };
}

const SORT_COLUMNS = Object.keys(SORTING_VALUES);
